import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchBox } from '@mapbox/search-js-react'
import { MapContainer, TileLayer, useMap } from 'react-leaflet'
import { useAddFarmContext, useAddFarmContextDispatch, setName, setLat, setLng, setZoom, setBounds } from './context/add-farm-context'

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN
const DEFAULT_ZOOM = 19
const MAPBOX_BASE_URL = 'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/512/{z}/{x}/{y}@2x'
const MAX_FARM_NAME_CHARS = 100

export const AddFarmModalPage1 = ({ header, onClose, onNext }) => {
  const { name, lat, lng, zoom } = useAddFarmContext()
  const dispatch = useAddFarmContextDispatch()

  const [targetLocation, setTargetLocation] = useState(() => {
    if (lat && lng && zoom) {
      return {lat, lng, zoom}
    }
    return null
  }, [lat, lng, zoom])
  const [nameFieldBlurred, setNameFieldBlurred] = useState(false)
  const mapRef = useRef(null)
  const theme = useTheme()


  const nameFieldRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (nameFieldRef.current) nameFieldRef.current.focus()
    }, 0)
    return () => clearTimeout(timer)
  }, [])

  const handleSearch = (res) => {
    const [lng, lat] = res.features[0].geometry.coordinates
    const newLoc = { lat, lng, zoom: DEFAULT_ZOOM }
    setTargetLocation(newLoc)

    // Move the map manually once
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], DEFAULT_ZOOM)
    }
  }

  const _onClose = useCallback(() => {
    setTargetLocation(null)
    setNameFieldBlurred(null)
    onClose && onClose()
  }, [onClose])

  const _onNext = async () => {
    onNext()
  }

  // triggered on every type of map change
  const handleMapChange = ({ lat, lng, zoom, bounds }) => {
    dispatch(setLat(lat))
    dispatch(setLng(lng))
    dispatch(setZoom(zoom))
    dispatch(setBounds(bounds))
  }

  return (
    <Box
      component="form"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '80vw', md: '60vw', lg: '50vw', xl: '40vw' },
        bgcolor: 'background.paper',
        borderRadius: '0.5rem',
        boxShadow: 24,
        p: '1rem 2rem',
      }}
    >
      {header}

      <Box sx={{ width: '100%', pt: '1rem' }}>
        <TextField
          inputRef={nameFieldRef}
          id="farm-name-field"
          label="Farm Name"
          error={!name && nameFieldBlurred}
          fullWidth
          value={name || ''}
          slotProps={{ htmlInput: {style: { resize: 'none' }, maxLength: MAX_FARM_NAME_CHARS } }}
          onChange={(event) => dispatch(setName(event.target.value.slice(0, MAX_FARM_NAME_CHARS) || ''))}
          onBlur={() => setNameFieldBlurred(true)}
          />
      </Box>

      <Typography id="modal-map-instructions" variant="h6" sx={{ py: '0.5rem' }}>
        Set Location
      </Typography>

      <Box
        sx={{
          px: '0.5rem',
          '& input': {
            color: `${theme.palette.text.primary} !important`,
            '&:focus, &:focus-visible': {
              outline: 'none !important',
              border: `2px solid ${theme.palette.primary.main} !important`,
            }
          }
        }}
      >
        <SearchBox
          accessToken={mapboxToken}
          onRetrieve={handleSearch}
          theme={{
            variables: {
              fontFamily: theme.typography.fontFamily,
              unit: '1rem',
              borderRadius: '8px',
              colorPrimary: theme.palette.primary.main,
              colorText: theme.palette.mode === 'dark' ? '#ffffff' : '#212121',
              colorSecondaryText: theme.palette.text.secondary,
              colorBackground: theme.palette.background.paper,
              colorBackgroundHover: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
              colorBackgroundActive: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0px 2px 4px rgba(0, 0, 0, 0.3)'
                : '0px 2px 4px rgba(0, 0, 0, 0.1)',
            }
          }}
        />

        {targetLocation?.lat && targetLocation?.lng && (
          <Stack px={2} pt={1}>
            <Typography id="modal-map-instructions" variant="body1" sx={{ textAlign: 'center' }}>
              Pan & zoom to {' '}
              <strong><span style={{ color: theme.palette.secondary.light }}>
                capture your entire planting area
              </span></strong> in the map as your complete bounding box
            </Typography>
  
            <Box
              sx={{
                aspectRatio: '1 / 1',
                width: '100%',
                maxWidth: '40vh',
                pt: '1rem',
                overflow: 'hidden',
                margin: '0 auto',
              }}
            >
              <MapContainer
                ref={mapRef}
                key={`${targetLocation.lat}-${targetLocation.lng}`}
                center={[targetLocation.lat, targetLocation.lng]}
                zoom={targetLocation.zoom}
                maxZoon={22}
                zoomSnap={0.25}
                zoomDelta={0.25}
                style={{ height: '100%', width: '100%' }}
                whenReady={(mapInstance) => {
                  // Capture initial bounds when map is ready
                  const map = mapInstance.target
                  const { lat, lng } = map.getCenter()
                  const zoom = map.getZoom()
                  const bounds = map.getBounds()
                  handleMapChange({ lat, lng, zoom, bounds })
                }}
              >
                <TileLayer
                  url={`${MAPBOX_BASE_URL}?access_token=${mapboxToken}`}
                  maxNativeZoom={20}
                  maxZoom={22}
                  detectRetina={true}
                  tileSize={512}
                  zoomOffset={-1}
                />
                <MapObserver onChange={handleMapChange} />
              </MapContainer>
            </Box>
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          mt: '2rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}
      >
        <Button variant='outlined' size='large' onClick={_onClose}>
          Cancel
        </Button>
        <Button disabled={!name} size='large' onClick={_onNext}>
          Next
        </Button>
      </Box>
    </Box>
  )
}

function MapObserver({ onChange }) {
  const map = useMap()

  useEffect(() => {
    const handleUpdate = () => {
      const { lat, lng } = map.getCenter()
      const zoom = map.getZoom()
      const bounds = map.getBounds()
      onChange && onChange({ lat, lng, zoom, bounds })
    }

    // Register event listeners
    map.on('moveend', handleUpdate)
    map.on('zoomend', handleUpdate)

    // Cleanup on unmount
    return () => {
      map.off('moveend', handleUpdate)
      map.off('zoomend', handleUpdate)
    }
  }, [map, onChange])

  return null
}